import React, { useEffect, forwardRef, useRef, useContext } from 'react';
import classNames from 'classnames';
import { dryEditorClass } from '../editor';
import { GlobalStateContext } from '../../state/app/index';

const Editor = forwardRef((props, ref) => {
  let isFirstRun = useRef(true);
  const editorClasses = classNames('note-editor-body-chrome');
  const globalServices = useContext(GlobalStateContext);

  // Remove stale handler on unmount so it cannot fire when the next note's
  // editor initializes (initializeEditorView dispatches a setCursorPosition
  // transaction that fires all registered handlers before the new handler is
  // registered, causing the old closure to save the old note with the new
  // note's content).
  useEffect(() => {
    return () => {
      dryEditorClass.registeredTransactionHandlers.delete('change');
      dryEditorClass.registeredTransactionHandlers.delete('paste');
    };
  }, []);

  useEffect(() => {
    if (!globalServices.appState) {
      return;
    }

    const {
      getEditorStateJSON,
      initializeEditorView,
      registerTransactionHandler,
    } = dryEditorClass;
    let editorState = { editorRef: ref };

    // Register the handler BEFORE initializeEditorView so that transactions
    // fired during initialization (e.g. setCursorPosition's setSelection
    // dispatch) call the current note's handler, not a stale one from the
    // previously-open note.
    if (props.onChange) {
      registerTransactionHandler({
        name: 'change',
        method: props.onChange,
      });

    }

    if (isFirstRun.current) {
      editorState.focus =
        typeof props.focus !== 'undefined' ? props.focus : true;
      editorState.value =
        typeof props.value !== 'undefined'
          ? props.value
          : '{"type":"doc","content":[]}';
      editorState.cursorPosition =
        typeof props.cursorPosition !== 'undefined' ? props.cursorPosition : 0;
      editorState.editable =
        typeof props.editable !== 'undefined' ? props.editable : false;

      if (typeof props.className !== 'undefined') {
        editorState.className = props.className;
      }

      initializeEditorView({ ...editorState, globalServices });
      isFirstRun.current = false;
    }

    if (props.onSave) {
      props.onSave(getEditorStateJSON());
    }
  }, [ref, props, globalServices]);

  return <div ref={ref} className={editorClasses} />;
});

Editor.displayName = 'Editor';

export default Editor;
